import React, { useEffect, useMemo, useRef, useState } from "react";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Autocomplete,
  Chip,
  Paper,
  Box,
  Typography,
  InputAdornment,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SearchIcon from "@mui/icons-material/Search";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import muiTheme from "../theme/muiTheme";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";

// ==========================================================
// ALTERAÇÕES CONTRATUAIS - ETAPAS DO FLUXO (seção 3.1)
// Responsável pela etapa = usuário escolhido. A permissão de cada
// usuário já é definida no cadastro de usuários do sistema.
// A carga inicial das 5 etapas é feita pela migration.
// ==========================================================

const API_ETAPAS = "/api/alteracoes-contratuais/etapas";

async function chamarApi(caminho = "", opcoes = {}) {
  const tk = localStorage.getItem("token");
  const resposta = await fetch(`${API_ETAPAS}${caminho}`, {
    ...opcoes,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}`, ...(opcoes.headers || {}) },
  });
  const dados = await resposta.json().catch(() => ({}));
  if (!resposta.ok) throw new Error(dados.erro || "Não foi possível concluir a operação.");
  return dados;
}

const formVazio = { nome: "", ordem: "", ativo: true, usuarios: [] };

const proximaOrdemSugerida = (lista) => (lista.length ? Math.max(...lista.map((e) => e.ordem)) + 1 : 1);

const normalizar = (texto) =>
  String(texto ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

const estiloCabecalho = {
  backgroundColor: "var(--ig-f-1e1e1e)",
  color: "var(--ig-t-ffffff)",
  fontWeight: "bold",
  borderBottom: "1px solid var(--ig-b-444444)",
  whiteSpace: "nowrap",
};

const estiloBotaoAcao = (cor, corHover) => ({
  backgroundColor: cor,
  color: "#fff",
  borderRadius: "4px",
  mx: 0.25,
  width: "auto",
  "&:hover": { backgroundColor: corHover },
  "&.Mui-disabled": { backgroundColor: "var(--ig-f-333333)", color: "var(--ig-t-666666)" },
});

const estiloCampo = (somenteLeitura, erro) => ({
  "& .MuiInputLabel-root": { color: erro ? "#f44336" : "var(--ig-t-ffffff)", fontWeight: "bold" },
  "& .MuiOutlinedInput-root": {
    backgroundColor: somenteLeitura ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
    color: "var(--ig-t-ffffff)",
    "& fieldset": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : erro ? "#f44336" : "var(--ig-b-444444)" },
    "&:hover fieldset": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : erro ? "#f44336" : "var(--ig-b-888888)" },
  },
  "& input": { color: "var(--ig-t-ffffff)" },
  "& .MuiFormHelperText-root": { color: "#f44336" },
});

const estiloSelect = (somenteLeitura, erro) => ({
  backgroundColor: somenteLeitura ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
  color: "var(--ig-t-ffffff)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : erro ? "#f44336" : "var(--ig-b-444444)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : erro ? "#f44336" : "var(--ig-b-888888)" },
  "& .MuiSvgIcon-root": { color: somenteLeitura ? "transparent" : "var(--ig-t-ffffff)" },
});

export default function SocietarioEtapasFluxo() {
  const [etapas, setEtapas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [processando, setProcessando] = useState(false);

  const [inputFiltro, setInputFiltro] = useState("");
  const [filtro, setFiltro] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(10);
  const [gridMinimizado, setGridMinimizado] = useState(false);

  const nomeInputRef = useRef(null);

  // form inferior: null = novo, valor = id selecionado
  const [editandoId, setEditandoId] = useState(null);
  const [somenteLeitura, setSomenteLeitura] = useState(false);
  const [form, setForm] = useState({ ...formVazio, ordem: "1" });
  const [errosForm, setErrosForm] = useState({});

  const [confirmDelete, setConfirmDelete] = useState({ aberto: false, id: null });

  const [snack, setSnack] = useState({ aberto: false, msg: "", severity: "success" });
  const mostrarSnack = (msg, severity = "success") => setSnack({ aberto: true, msg, severity });

  // permissões (níveis do sistema)
  const token = localStorage.getItem("token");
  let tipoUsuario = "";
  if (token) {
    try {
      const decoded = jwtDecode(token);
      tipoUsuario = normalizar(decoded.tipo);
    } catch (_) {}
  }
  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["administrador"].includes(tipoUsuario);

  // Usuários ativos da empresa (mesma consulta usada em Alçadas de Aprovação).
  useEffect(() => {
    const tk = localStorage.getItem("token");
    fetch("/api/alcadas/usuarios", { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => r.json())
      .then((data) => setUsuarios(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Erro ao buscar usuários:", err));
  }, []);

  // Carrega as etapas gravadas no banco.
  const carregarEtapas = async () => {
    setCarregando(true);
    try {
      const lista = await chamarApi();
      const etapasBanco = Array.isArray(lista) ? lista : [];
      setEtapas(etapasBanco);
      return etapasBanco;
    } catch (erro) {
      mostrarSnack(erro.message, "error");
      return null;
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (!podeVisualizar) return;
    carregarEtapas().then((lista) => {
      if (lista) setForm((p) => (editandoId ? p : { ...p, ordem: String(proximaOrdemSugerida(lista)) }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeVisualizar]);

  // ── Pesquisa automática
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltro(inputFiltro);
      setPagina(0);
    }, 400);
    return () => clearTimeout(t);
  }, [inputFiltro]);

  const etapasOrdenadas = useMemo(() => [...etapas].sort((a, b) => a.ordem - b.ordem), [etapas]);

  const textoResponsaveis = (etapa) => etapa.usuarios.map((u) => u.nome).join(" | ") || "—";

  const etapasFiltradas = useMemo(() => {
    const termo = normalizar(filtro);
    if (!termo) return etapasOrdenadas;
    return etapasOrdenadas.filter((e) => [e.ordem, e.nome, textoResponsaveis(e), e.ativo ? "ativa" : "inativa"].some((v) => normalizar(v).includes(termo)));
  }, [etapasOrdenadas, filtro]);

  const etapasPagina = etapasFiltradas.slice(pagina * porPagina, pagina * porPagina + porPagina);

  const handlePesquisar = () => {
    setFiltro(inputFiltro);
    setPagina(0);
  };
  const handleLimparFiltro = () => {
    setInputFiltro("");
    setFiltro("");
    setPagina(0);
  };

  const handleCopiar = async () => {
    const texto = etapasFiltradas.map((e) => `${e.ordem}º - ${e.nome}`).join(", ");
    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }
    const ok = await copiarTexto(texto);
    alert(ok ? "Copiado!" : "Não foi possível copiar.");
  };

  const handleCSV = () => {
    const BOM = "﻿";
    const header = ["Ordem", "Etapa", "Responsáveis", "Situação"];
    const csvEscape = (value) => {
      const texto = String(value ?? "");
      return /[;"\n\r]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
    };
    const linhas = etapasFiltradas.map((e) => [e.ordem, e.nome, textoResponsaveis(e), e.ativo ? "Ativa" : "Inativa"].map(csvEscape));
    const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");
    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "etapas-fluxo-alteracoes-contratuais.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  // ── Visualizar (preenche form inferior)
  const handleVisualizar = (etapa) => {
    setEditandoId(etapa.id);
    setSomenteLeitura(true);
    setForm({ nome: etapa.nome, ordem: String(etapa.ordem), ativo: etapa.ativo, usuarios: [...etapa.usuarios] });
    setErrosForm({});
    setGridMinimizado(true);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const handleHabilitarEdicao = () => {
    setSomenteLeitura(false);
    setTimeout(() => nomeInputRef.current?.focus(), 50);
  };

  const handleCancelarForm = (lista = etapas) => {
    setEditandoId(null);
    setSomenteLeitura(false);
    setForm({ ...formVazio, ordem: String(proximaOrdemSugerida(lista)) });
    setErrosForm({});
    setGridMinimizado(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const validarForm = () => {
    const erros = {};
    const nome = form.nome.trim();
    const ordem = Number(form.ordem);

    if (!nome) erros.nome = "Nome da etapa é obrigatório.";
    else if (etapas.some((e) => e.id !== editandoId && normalizar(e.nome) === normalizar(nome))) erros.nome = "Já existe uma etapa com este nome.";

    if (!form.ordem) erros.ordem = "Ordem é obrigatória.";
    else if (!Number.isInteger(ordem) || ordem <= 0) erros.ordem = "Ordem deve ser um número inteiro positivo.";
    else if (etapas.some((e) => e.id !== editandoId && e.ordem === ordem)) erros.ordem = `Ordem ${ordem} já está em uso por outra etapa.`;

    if (form.usuarios.length === 0) erros.responsavel = "Informe ao menos um usuário responsável.";

    return erros;
  };

  // ── Salvar (criar ou editar)
  const handleSalvar = async () => {
    if (processando) return;
    const erros = validarForm();
    if (Object.keys(erros).length > 0) {
      setErrosForm(erros);
      nomeInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setErrosForm({});

    const corpo = {
      nome: form.nome.trim().toUpperCase(),
      ordem: Number(form.ordem),
      ativo: form.ativo,
      usuarios: form.usuarios.map((u) => u.id),
    };

    setProcessando(true);
    try {
      await chamarApi(editandoId ? `/${editandoId}` : "", { method: editandoId ? "PUT" : "POST", body: JSON.stringify(corpo) });
      mostrarSnack(editandoId ? "Etapa atualizada!" : "Etapa cadastrada com sucesso!");
      const lista = await carregarEtapas();
      handleCancelarForm(lista || etapas);
    } catch (erro) {
      mostrarSnack(erro.message, "error");
    } finally {
      setProcessando(false);
    }
  };

  // ── Reordenar: troca a ordem com a etapa vizinha (no banco)
  const handleMover = async (etapa, direcao) => {
    if (processando) return;
    setProcessando(true);
    try {
      const lista = await chamarApi(`/${etapa.id}/mover`, { method: "PUT", body: JSON.stringify({ direcao }) });
      setEtapas(Array.isArray(lista) ? lista : []);
      const atualizada = (lista || []).find((e) => e.id === editandoId);
      if (atualizada) setForm((p) => ({ ...p, ordem: String(atualizada.ordem) }));
    } catch (erro) {
      mostrarSnack(erro.message, "error");
    } finally {
      setProcessando(false);
    }
  };

  // ── Excluir
  const handleConfirmarDelete = (id) => {
    document.activeElement?.blur();
    setConfirmDelete({ aberto: true, id });
  };

  const handleExcluirConfirmado = async () => {
    const { id } = confirmDelete;
    setConfirmDelete({ aberto: false, id: null });
    try {
      await chamarApi(`/${id}`, { method: "DELETE" });
      const lista = (await carregarEtapas()) || etapas.filter((e) => e.id !== id);
      if (editandoId === id) handleCancelarForm(lista);
      else if (!editandoId) setForm((p) => ({ ...p, ordem: String(proximaOrdemSugerida(lista)) }));
      mostrarSnack("Etapa excluída.", "warning");
    } catch (erro) {
      mostrarSnack(erro.message, "error");
    }
  };

  if (!podeVisualizar) {
    return (
      <ThemeProvider theme={muiTheme}>
        <Box sx={{ color: "var(--ig-t-ffffff)" }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Etapas do Fluxo
          </Typography>
          <Alert severity="warning">Você não possui permissão para acessar esta tela.</Alert>
        </Box>
      </ThemeProvider>
    );
  }

  const etapasSemResponsavel = etapasOrdenadas.filter((e) => e.ativo && e.usuarios.length === 0);

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "var(--ig-t-ffffff)" }}>
        {/* Título */}
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Etapas do Fluxo
        </Typography>

        {etapasSemResponsavel.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {etapasSemResponsavel.map((e) => `${e.ordem}º - ${e.nome}`).join(", ")}: etapa ativa sem responsável definido. Escolha o usuário responsável.
          </Alert>
        )}

        {/* ── Filtro ─────────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Filtrar Etapa"
            value={inputFiltro}
            onChange={(e) => setInputFiltro(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handlePesquisar();
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "var(--ig-t-9ca3af)", fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": { backgroundColor: "transparent", color: "var(--ig-t-ffffff)" },
              "& input::placeholder": { color: "var(--ig-t-ffffff)", opacity: 1, fontWeight: 500, fontSize: "1rem" },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            type="button"
            onClick={handleLimparFiltro}
            sx={{
              flexShrink: 0,
              width: "auto",
              height: 40,
              color: "#2196f3",
              borderColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": { borderColor: "#42a5f5", backgroundColor: "rgba(33, 150, 243, 0.08)" },
            }}
          >
            Limpar Filtro
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCopiar}
            sx={{ height: 40, backgroundColor: "#2196f3", fontWeight: 600, textTransform: "none", "&:hover": { backgroundColor: "#1976d2" } }}
          >
            <i className="fas fa-copy" style={{ marginRight: 6 }} />
            Copiar
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCSV}
            sx={{ height: 40, backgroundColor: "#2196f3", fontWeight: 600, textTransform: "none", "&:hover": { backgroundColor: "#1976d2" } }}
          >
            CSV
          </Button>
        </Box>

        {/* ── Minimizar grid ───────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 1 }}>
          {!gridMinimizado ? (
            <button
              type="button"
              onClick={() => setGridMinimizado(true)}
              style={{
                border: "1px solid #d32f2f",
                color: "#ff4c4c",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setGridMinimizado(false)}
              style={{
                border: "1px solid #1998a8",
                color: "#1998a8",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↗ Expandir
            </button>
          )}
        </Box>

        {/* ── Grid ─────────────────────────────────────────────────────────── */}
        <Collapse in={!gridMinimizado}>
          <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: "transparent", boxShadow: "none" }}>
            <Table size="small" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={estiloCabecalho}>Ações</TableCell>
                  <TableCell align="center" sx={estiloCabecalho}>
                    Ordem
                  </TableCell>
                  <TableCell sx={estiloCabecalho}>Etapa</TableCell>
                  <TableCell sx={{ ...estiloCabecalho, width: "100%" }}>Responsáveis</TableCell>
                  <TableCell align="center" sx={estiloCabecalho}>
                    Situação
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {etapasPagina.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: "var(--ig-t-888888)", py: 3, borderBottom: "1px solid var(--ig-b-444444)" }}>
                      {carregando ? "Carregando etapas..." : "Nenhuma etapa encontrada."}
                    </TableCell>
                  </TableRow>
                ) : (
                  etapasPagina.map((etapa) => {
                    const indice = etapasOrdenadas.findIndex((e) => e.id === etapa.id);
                    return (
                      <TableRow
                        key={etapa.id}
                        sx={{
                          "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" },
                          backgroundColor: editandoId === etapa.id ? "rgba(25,152,168,0.08)" : "transparent",
                        }}
                      >
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                          <Tooltip title="Visualizar / Editar">
                            <IconButton size="small" onClick={() => handleVisualizar(etapa)} sx={estiloBotaoAcao("#1998a8", "#1565c0")}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {podeCadastrar && (
                            <>
                              <Tooltip title="Subir etapa">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={indice <= 0 || Boolean(filtro)}
                                    onClick={() => handleMover(etapa, -1)}
                                    sx={estiloBotaoAcao("#555", "#444")}
                                  >
                                    <ArrowUpwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Descer etapa">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={indice >= etapasOrdenadas.length - 1 || Boolean(filtro)}
                                    onClick={() => handleMover(etapa, 1)}
                                    sx={estiloBotaoAcao("#555", "#444")}
                                  >
                                    <ArrowDownwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                          {podeExcluir && (
                            <Tooltip title="Excluir etapa">
                              <IconButton size="small" onClick={() => handleConfirmarDelete(etapa.id)} sx={estiloBotaoAcao("#d32f2f", "#c62828")}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)", whiteSpace: "nowrap" }}>
                          {etapa.ordem}º
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)", whiteSpace: "nowrap" }}>
                          {etapa.nome}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)" }}>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {etapa.usuarios.map((u) => (
                              <Chip key={u.id} size="small" label={u.nome} sx={{ backgroundColor: "#1998a8", color: "#fff", fontWeight: "bold" }} />
                            ))}
                            {etapa.usuarios.length === 0 && <span style={{ color: "#ed6c02" }}>Sem responsável</span>}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                          <Chip
                            size="small"
                            label={etapa.ativo ? "Ativa" : "Inativa"}
                            sx={{ backgroundColor: etapa.ativo ? "#2e7d32" : "#555", color: "#fff", fontWeight: "bold" }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={etapasFiltradas.length}
              page={pagina}
              onPageChange={(_, p) => setPagina(p)}
              rowsPerPage={porPagina}
              onRowsPerPageChange={(e) => {
                setPorPagina(parseInt(e.target.value, 10));
                setPagina(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Etapas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{
                color: "var(--ig-t-ffffff)",
                borderTop: "1px solid var(--ig-b-444444)",
                "& .MuiTablePagination-selectIcon": { color: "var(--ig-t-ffffff)" },
                "& .MuiTablePagination-actions button": { color: "var(--ig-t-ffffff)", width: "auto" },
                "& .MuiTablePagination-actions": { display: "flex", alignItems: "center" },
              }}
            />
          </TableContainer>
        </Collapse>

        {/* ── Formulário inferior (visualizar / criar / editar) ────────────── */}
        {(podeCadastrar || (podeVisualizar && editandoId)) && (
          <Paper sx={{ p: 3, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, boxShadow: "0 0 20px rgba(0,0,0,0.4)" }}>
            <Typography variant="h6" sx={{ mb: 2, color: "var(--ig-t-ffffff)", fontWeight: 600 }}>
              {editandoId && somenteLeitura ? "Visualizar Etapa" : editandoId ? "Editar Etapa" : "Nova Etapa do Fluxo"}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Nome da Etapa*"
                  size="small"
                  inputRef={nomeInputRef}
                  value={form.nome}
                  onChange={(e) => {
                    if (somenteLeitura) return;
                    setForm((p) => ({ ...p, nome: e.target.value.toUpperCase() }));
                    if (errosForm.nome) setErrosForm((p) => ({ ...p, nome: undefined }));
                  }}
                  error={!!errosForm.nome}
                  helperText={errosForm.nome}
                  slotProps={{ input: { readOnly: somenteLeitura }, htmlInput: { maxLength: 100 } }}
                  sx={{ flex: 3, minWidth: 240, ...estiloCampo(somenteLeitura, errosForm.nome) }}
                />

                <TextField
                  label="Ordem*"
                  size="small"
                  type="number"
                  value={form.ordem}
                  onChange={(e) => {
                    if (somenteLeitura) return;
                    setForm((p) => ({ ...p, ordem: e.target.value }));
                    if (errosForm.ordem) setErrosForm((p) => ({ ...p, ordem: undefined }));
                  }}
                  error={!!errosForm.ordem}
                  helperText={errosForm.ordem}
                  slotProps={{ input: { readOnly: somenteLeitura }, htmlInput: { min: 1 } }}
                  sx={{ flex: 1, minWidth: 120, ...estiloCampo(somenteLeitura, errosForm.ordem) }}
                />

                <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
                  <InputLabel sx={{ color: "var(--ig-t-ffffff)", fontWeight: "bold" }}>Situação*</InputLabel>
                  <Select
                    label="Situação*"
                    value={form.ativo ? "ATIVA" : "INATIVA"}
                    onChange={(e) => !somenteLeitura && setForm((p) => ({ ...p, ativo: e.target.value === "ATIVA" }))}
                    inputProps={{ readOnly: somenteLeitura }}
                    sx={estiloSelect(somenteLeitura, false)}
                  >
                    <MenuItem value="ATIVA">Ativa</MenuItem>
                    <MenuItem value="INATIVA">Inativa</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Typography variant="subtitle2" sx={{ color: "var(--ig-t-cccccc)", fontWeight: 600 }}>
                Responsáveis por avançar a alteração desta etapa para a próxima
              </Typography>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Autocomplete
                  multiple
                  size="small"
                  readOnly={somenteLeitura}
                  forcePopupIcon={!somenteLeitura}
                  options={usuarios}
                  getOptionLabel={(u) => u.nome}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={form.usuarios}
                  onChange={(_, val) => {
                    if (somenteLeitura) return;
                    setForm((p) => ({ ...p, usuarios: val }));
                    if (errosForm.responsavel) setErrosForm((p) => ({ ...p, responsavel: undefined }));
                  }}
                  renderTags={(valores, getTagProps) =>
                    valores.map((u, i) => (
                      <Chip {...getTagProps({ index: i })} key={u.id} size="small" label={u.nome} sx={{ backgroundColor: "#1998a8", color: "#fff" }} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Usuários Responsáveis*" size="small" sx={estiloCampo(somenteLeitura, errosForm.responsavel)} />
                  )}
                  sx={{ flex: 2, minWidth: 280 }}
                />
              </Box>
              {errosForm.responsavel && <FormHelperText sx={{ color: "#f44336", mt: -1 }}>{errosForm.responsavel}</FormHelperText>}

              <Typography variant="body2" sx={{ color: "var(--ig-t-9ca3af)" }}>
                Somente quem estiver cadastrado como responsável pela etapa atual poderá avançar a alteração contratual para a próxima etapa. Etapas inativas
                são ignoradas no fluxo.
              </Typography>

              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                {/* Modo visualização */}
                {editandoId && somenteLeitura && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => handleCancelarForm()}
                      sx={{
                        width: "auto",
                        borderColor: "var(--ig-b-555555)",
                        color: "var(--ig-t-cccccc)",
                        "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                        fontWeight: "bold",
                        px: 3,
                      }}
                    >
                      Fechar
                    </Button>
                    {podeCadastrar && (
                      <Button
                        variant="contained"
                        startIcon={<FaEdit />}
                        onClick={handleHabilitarEdicao}
                        sx={{ width: "auto", backgroundColor: "#1998a8", "&:hover": { backgroundColor: "#1565c0" }, fontWeight: "bold", py: 1, px: 3 }}
                      >
                        Editar
                      </Button>
                    )}
                    {podeExcluir && (
                      <Button
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleConfirmarDelete(editandoId)}
                        sx={{ width: "auto", backgroundColor: "#d32f2f", "&:hover": { backgroundColor: "#c62828" }, fontWeight: "bold", py: 1, px: 3 }}
                      >
                        Excluir
                      </Button>
                    )}
                  </>
                )}
                {/* Modo edição / criação */}
                {(!editandoId || !somenteLeitura) && (
                  <>
                    {editandoId && (
                      <Button
                        variant="outlined"
                        startIcon={<FaTimes />}
                        onClick={() => handleCancelarForm()}
                        sx={{
                          width: "auto",
                          borderColor: "var(--ig-b-555555)",
                          color: "var(--ig-t-cccccc)",
                          "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                          fontWeight: "bold",
                          px: 3,
                        }}
                      >
                        Cancelar
                      </Button>
                    )}
                    {podeCadastrar && (
                      <Button
                        variant="contained"
                        startIcon={<FaSave />}
                        onClick={handleSalvar}
                        disabled={processando}
                        sx={{ width: "auto", backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" }, fontWeight: "bold", py: 1, px: 4 }}
                      >
                        {editandoId ? "Atualizar" : "Cadastrar"}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* ── Dialog: Confirmação de exclusão ──────────────────────────────── */}
      <Dialog open={confirmDelete.aberto} onClose={() => setConfirmDelete({ aberto: false, id: null })}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "var(--ig-t-cccccc)" }}>
            Deseja excluir esta etapa do fluxo? Se ela já estiver em uso por alguma alteração contratual, prefira inativá-la.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmDelete({ aberto: false, id: null })}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleExcluirConfirmado}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.aberto}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, aberto: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, aberto: false }))} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
